import { useEffect, useRef, useState } from 'react';

// Contract shared with the embed route of the tenant sites. Kept in sync by
// hand with packages/nw4-tenant-pages/src/embed/embedContract.ts over there.
const MESSAGE_CHANNEL = 'nw4.embed.header';
const MESSAGE_VERSION = 1;

const isResizeMessage = (data) =>
  Boolean(data) &&
  data.channel === MESSAGE_CHANNEL &&
  data.v === MESSAGE_VERSION &&
  data.type === 'resize' &&
  Number.isFinite(data.height) &&
  data.height > 0;

/**
 * Tracks the height reported by the embedded site header.
 *
 * The header never blocks the form: until the first message arrives the frame
 * keeps the configured reserve, and if nothing arrives within the timeout it is
 * dropped for good. A blank frame is worse than no header, and retrying against
 * a tenant that is down would only multiply the load.
 *
 * @param {object} params
 * @param {string|null} params.url - The validated embed URL, `null` to do nothing
 * @param {HTMLIFrameElement|null} params.frame - The iframe element to listen to
 * @param {number} params.timeout - Milliseconds before giving up
 */
export const useSiteHeaderFrame = ({ frame, timeout, url }) => {
  const [height, setHeight] = useState(null);
  const [hasFailed, setHasFailed] = useState(false);
  const hasAnsweredRef = useRef(false);

  useEffect(() => {
    hasAnsweredRef.current = false;
    setHeight(null);
    setHasFailed(false);
  }, [url]);

  useEffect(() => {
    if (!url) {
      return undefined;
    }

    const expectedOrigin = new URL(url).origin;

    const handleMessage = (event) => {
      // The source check is the one that matters: origins cannot be spoofed,
      // but another frame on the same origin could otherwise talk to us.
      if (event.source !== frame?.contentWindow || event.origin !== expectedOrigin) {
        return;
      }

      if (!isResizeMessage(event.data)) {
        return;
      }

      hasAnsweredRef.current = true;
      setHeight(event.data.height);
    };

    window.addEventListener('message', handleMessage);

    const timeoutId = window.setTimeout(() => {
      if (!hasAnsweredRef.current) {
        setHasFailed(true);
        console.warn('[nw4] site header did not respond, hiding it', url);
      }
    }, timeout);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(timeoutId);
    };
  }, [frame, timeout, url]);

  return { hasFailed, height };
};
